<template>
  <v-app>
    <div class="hidden-print-only">
      <!-- Navigation Drawer -->
      <v-navigation-drawer
        v-if="isAuthenticated"
        v-model="sideNav"
        class="hidden-md-and-up"
        temporary
        app>
        <hg-mobile-menu />
      </v-navigation-drawer>

      <!-- Header -->
      <hg-header
        v-if="!enabledWelcomeFlowFooter && !$store.state.unleash.loading"
        class="hidden-sm-and-down main-header"
        @hamburger-click="sideNav = !sideNav"
        :hg-authenticated="isAuthenticated"
        :hg-current-page="page"
        :hg-user="hgUser"/>

      <div v-show="!loaded">
        <v-container>
          <v-layout flex align-center justify-center>
            <v-progress-circular
              class="spinner"
              :size="70"
              :width="7"
              :indeterminate="true" />
          </v-layout>
        </v-container>
      </div>
    </div>

    <!-- Doing soft delete, maybe we'll want the code in the future, who knows. -->
    <OffersModal v-if="false" />

    <!-- Main Body -->
    <v-content
      v-if="hydrated && !enabledWelcomeFlowFooter && !$store.state.unleash.loading"
      class="dashboard-body">
      <!-- Hamburger Icon -->
      <v-toolbar-side-icon
        v-if="isAuthenticated"
        @click="hamburgerClick"
        class="menu-button hidden-md-and-up"
        fixed>
        <img src="./assets/icons/hamburger.png" alt="menu icon" />
      </v-toolbar-side-icon>
      <Banner
      :isFloatingBanner="$route.name === 'reportView'"
      v-if="showBanner && $route.name"
      :location="bannerLocationRouteMapper[$route.name]"
      :referrer="bannerLocationRouteMapper[$route.name]"
      @closeBannerEvent="closeBanner"></Banner>
      <v-container
        id="mainBody"
        fill-height
        :class="['main-body',
        showBanner ? 'expand': '']">
        <v-layout class="boundary-box">
          <v-flex x12>
            <router-view @userUpdated="userUpdated"></router-view>
          </v-flex>
        </v-layout>
      </v-container>
    </v-content>

    <new-header v-if="enabledWelcomeFlowFooter" />

    <router-view
      v-if="enabledWelcomeFlowFooter"
      @userUpdated="userUpdated">
    </router-view>

    <new-footer v-if="enabledWelcomeFlowFooter" />

    <PrivacyPolicyToast />

    <!-- Footer -->
    <div
      v-if="!enabledWelcomeFlowFooter && !$store.state.unleash.loading"
      class="hidden-print-only">
      <hg-footer app />
    </div>
  </v-app>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import OffersClient from '@/utilities/offers-client';

import OffersModal from './components/OffersModal.vue';
import PrivacyPolicyToast from './components/PrivacyPolicyToast.vue';
import Header from './components/Layout/Header.vue';
import NewHeader from './components/Layout/Upcoming/Header.vue';
import Footer from './components/Layout/Footer.vue';
import NewFooter from './components/Layout/Upcoming/Footer.vue';
import MobileMenu from './components/Layout/Menu/MobileMenu.vue';
import WelcomeRoute from './router/routes/welcome.route';
import Banner from './components/Pages/Banner.vue';
import BaseViewComponentVue from './components/Shared/BaseViewComponent.vue';

export default {
  name: 'App',
  extends: BaseViewComponentVue,
  components: {
    'hg-header': Header,
    'new-header': NewHeader,
    'hg-footer': Footer,
    'new-footer': NewFooter,
    'hg-mobile-menu': MobileMenu,
    OffersModal,
    PrivacyPolicyToast,
    Banner,
  },
  data() {
    return {
      hgUser: {},
      hydrated: false,
      page: '',
      sideNav: false,
      clients: {},
      isDueForDisplayWelcome: false,
      bannerLocationRouteMapper: {
        myInspections: 'inspection_list',
        inspection: 'inspection_details',
        reportView: 'report',
      },
    };
  },
  computed: {
    ...mapGetters('User', ['userAuthToken']),
    ...mapState({
      loaded: state => state.loaded,
    }),
    ...mapState('User', {
      isAuthenticated: state => state.isAuthenticated,
    }),
    ...mapState('unleash', {
      enabledFeatures: state => state.enabledFeatures,
    }),
    getRoutePath() {
      return this.$router.currentRoute.path;
    },
    showBanner() {
      const isBannerDismissed = JSON.parse(sessionStorage.getItem('bannerDismissed'));
      return !isBannerDismissed
        && this.enabledFeatures['WEB-5081-Show-Deals-Offer-Banners']
        && this.bannerLocationRouteMapper[this.$route.name];
    },
    isWelcomeEnabled() {
      return this.isDueForDisplayWelcome;
    },
    enabledWelcomeFlowFooter() {
      return (this.$route.path.startsWith('/welcome')) && this.isWelcomeEnabled;
    },
  },
  watch: {
    isWelcomeEnabled() {
      if (this.isWelcomeEnabled) {
        this.$router.push({ path: WelcomeRoute.path }).then();
      }
    },
  },
  methods: {
    hamburgerClick() {
      this.sideNav = true;
    },
    userUpdated(user) {
      this.hgUser = user;
    },
    closeBanner() {
      const mainBodyElement = this.$el.querySelector('#mainBody');
      mainBodyElement.classList.remove('expand');
    },
    initializeClients() {
      this.clients.offersClient = new OffersClient(this.userAuthToken);
    },
    async requestIsDueForDisplay() {
      await this.initializeClients();
      this.isDueForDisplayWelcome = await this.clients.offersClient.isDueForDisplay('welcome');
    },
  },
  async mounted() {
    await this.initialHydration([
      this.$store.dispatch('unleash/fetch'),
      this.requestIsDueForDisplay(),
      this.hydrated = true,
    ]);
  },
};
</script>

<style lang="scss" scoped>
@import './styles/vars/theme';

.container .layout {
  background-color: $theme-color-white;
  box-shadow: 0 8px 24px 0 rgba(0, 0, 0, 0.1);
}

.menu-button {
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: inherit;
  height: 50px;
  margin: 0;
  position: absolute;
  right: 0;
  width: 50px;
  z-index: 1000;
}

.dashboard-body {
  margin-bottom: 60px;
}

.main-header {
  max-height: 70px;
  padding: 0;
}

.main-body {
  padding-top: 0;
  &.expand {
    height: calc(100% - 68px);
  }
}

.spinner {
  color: $theme-color-primary;
  margin-bottom: 40px;
  margin-top: 40px;
}

*::-ms-backdrop,
.dashboard-body,
.v-footer {
  position: relative;
  top: 75px;
}

@media only screen and (max-width: 960px) {
  .container {
    padding: 0;
  }
}
</style>
